import { createFileRoute, Link, Outlet, useMatches } from '@tanstack/react-router'
import { Users, Shield, Wrench, Bot } from 'lucide-react'
import { motion } from 'framer-motion'
import { useIsSuperAdmin } from '@/stores/authStore'
import { cn } from '@/lib/utils'
import { pageVariants } from '@/pages/admin/shared'
import { useTranslation } from 'react-i18next'

const ADMIN_TABS = [
  { to: '/admin/team', segment: 'team', label: 'Team', icon: Users },
  { to: '/admin/access', segment: 'access', label: 'Access Control', icon: Shield },
  { to: '/admin/system', segment: 'system', label: 'System', icon: Wrench },
  { to: '/admin/ai-engine', segment: 'ai-engine', label: 'AI Engine', icon: Bot, superOnly: true },
] as const

export const Route = createFileRoute('/_authenticated/admin')({
  component: AdminLayout,
})

function AdminLayout() {
  const { t } = useTranslation()
  const isSuperAdmin = useIsSuperAdmin()
  const matches = useMatches()
  // Derive active tab from the match after the admin layout match
  // For /admin/system/rbac, we want 'system'; for /admin/team, we want 'team'
  const lastMatch = matches[matches.length - 1]
  const routeSegments = lastMatch?.routeId?.split('/') ?? []
  // The segment right after 'admin' in the route ID
  const adminIndex = routeSegments.indexOf('admin')
  const activeTab = adminIndex >= 0 && routeSegments.length > adminIndex + 1
    ? routeSegments[adminIndex + 1]
    : 'team'

  const visibleTabs = ADMIN_TABS.filter(tab => !tab.superOnly || isSuperAdmin)

  return (
    <motion.div variants={pageVariants} initial="hidden" animate="visible" className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('adminPage:admin')}</h1>
        <p className="text-muted-foreground mt-1">{t('adminPage:manageYourTeamAccessControlAndSystemOper')}</p>
      </div>

      <div className="flex gap-1 border-b">
        {visibleTabs.map(tab => (
          <Link
            key={tab.to}
            to={tab.to}
            className={cn(
              'flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              activeTab === tab.segment
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </Link>
        ))}
      </div>

      <Outlet />
    </motion.div>
  )
}
